/**
 * API is responsible to all logic related to endpoints.
 * It is responsible to get errors returned from endpoints in different formats and throw a single error message.
 * TODO: throw an object with message and optionally a code or other relevant information.
 */
import axios from "axios";
import {
  getHttpProtocol as scheme,
  getAppointmentsServieUrl as appointmentServiceUrl,
} from "../utils/requests";
import { AppointmentStatus } from "../pages/Appointments/Appointments.types";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import { fetchAllPages } from "../utils/pagination.utils";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";

// custom axios

const appointmentAxios = axios.create({
  baseURL: `${scheme}//${appointmentServiceUrl}`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

appointmentAxios.interceptors.request.use(requestAccessTokenInterceptor);

appointmentAxios.interceptors.request.use(refreshAccessTokenInterceptor);

appointmentAxios.interceptors.response.use(responseStatusInterceptor);

// entities types
export interface AppointmentType {
  staff: {
    uuid: string;
  }[];
  sendEmailReminder: boolean;
  emailReminderTiming: number;
  createdAt: string;
  sendTextReminder: boolean;
  lateCancel: number;
  name: string;
  sendEmailNotification: boolean;
  availability: {
    dayOfTheWeek: string;
    startTime: string;
    endTime: string;
  }[];
  memberStatus: string;
  minimumSchedulingNotice: number;
  textReminderTiming: number;
  client: string;
  updatedAt: string;
  uuid: string;
  duration: number;
  color: string;
  description?: string;
  bufferAfterAppointment: string;
  bufferBeforeAppointment: string;
  availabilityIncrements: string;
  hasNextSteps: boolean;
  nextStepsLabel: string;
  nextStepsUrl: string;
  emailNotificationSubject: string;
  message: string;
  emailReminderSubject: string;
  emailReminderMessage: string;
  notifyStaffMember: boolean;
  textReminderMessage: string;
}

export interface DiscountType {
  discountValue: number;
  createdAt: string;
  name: string;
  numberOfRebills: number;
  discountCode: string;
  appliesForXRebills: boolean;
  client: string;
  updatedAt: string;
  discountType: string;
  uuid: string;
  appliesForever: boolean;
  description: string;
}

export interface AppointmentPackage {
  taxId?: string;
  staffPayType: string;
  recurringInterval?: string;
  name: string;
  discount?: string;
  taxName?: string;
  numberOfSessions: number;
  isRecurring: boolean;
  hasTax: boolean;
  policydocuments: {
    uuid: string;
  }[];
  expirationForEachAppointmentCredit: number;
  createdAt: string;
  packagePrice: number;
  availableOnMemberApp: boolean;
  appointmentType: {
    name: string;
    uuid: string;
    color: string;
  };
  taxPrice: number;
  client: string;
  updatedAt: string;
  staffPay: number;
  uuid: string;
  description: string;
  isFree: boolean;
  longDescription?: string;
}

export interface AppointmentDiscount {
  uuid: string;
  client: string;
  name: string;
  description: string;
  appliesForever: boolean;
  appliesForXRebills: boolean;
  numberOfRebills: number;
  discountCode: string;
  discountType: string;
  discountValue: number;
  createdAt: string;
  updatedAt: string;
}

export interface AppointmentEvent {
  id: string;
  key: string;
  staff: {
    uuid: string;
    firstName: string;
    email: string;
    lastName: string;
    first_name: string;
    last_name: string;
  };
  startTime: number;
  duration: number;
  name: string;
  eventStatus:
  | "CANCELLED"
  | "CHECKEDIN"
  | "EXPIRED"
  | "NOTBOOKED"
  | "NOSHOW"
  | "SCHEDULED";
  appointmentType?: {
    staff: {
      uuid: string;
    }[];
    emailNotificationSubject: string;
    sendEmailReminder: boolean;
    sendTextReminder: boolean;
    bufferBeforeAppointment: string;
    lateCancel: number;
    textReminderMessage: string;
    sendEmailNotification: boolean;
    name: string;
    availability: {
      dayOfTheWeek: string;
      startTime: string;
      endTime: string;
    }[];
    minimumSchedulingNotice: number;
    hasNextSteps: boolean;
    textReminderTiming: number;
    nextStepsUrl: string;
    availabilityIncrements: string;
    emailReminderSubject: string;
    bufferAfterAppointment: number;
    SK: string;
    nextStepsLabel: string;
    duration: number;
    color: string;
    emailReminderTiming: number;
    createdAt: string;
    message: string;
    emailReminderMessage: string;
    client: string;
    updatedAt: string;
    uuid: string;
    PK: string;
  };
  eventType: string;
  start: string;
  end: string;
  day: string;
  uuid: string;
  user: {
    uuid: string;
    firstName: string;
    email: string;
    lastName: string;
    first_name: string;
    last_name: string;
  };
  appointmentCredit?: {
    uuid: string;
    usedAt: string;
    canExpire: boolean;
    expiresAt: string;
  };
}

export interface Staff {
  clientUserId: number;
  clientUuid: string;
  uuid: string;
  userUuid: string;
  assignedToUserId: number;
  parentUserId: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  nickname: string;
  status: string;
  dob?: string;
  externalProfileUrl: string;
  objective?: string;
  isLead: boolean;
  isMember: boolean;
  isStaff: boolean;
  active: boolean;
  archived: boolean;
  deleted: boolean;
  primaryImage: string;
  createdAt: string;
  updatedAt: string;
  joinedAt: string;
  lastCheckedInAt: string;
  activatedAt: string;
  membershipStatus: string;
  membershipRole: string;
  memberSince?: string;
  membershipPaused: number;
  code: string;
  display: string;
  paused: boolean;
  isSynced: boolean;
}

// endpoints

export interface GetAppointmentTypesResult {
  data: AppointmentType[];
  hasNext?: boolean;
  hasPrevious?: boolean;
  next?: string;
  previous?: string;
}

export async function getAppointmentTypes(clientUuid: string) {
  return fetchAllPages(async (next?: string) => {
    const url = `/appointment_type/${clientUuid}`;
    const response = await appointmentAxios.get<GetAppointmentTypesResult>(
      url,
      { params: { next } },
    );
    return response.data;
  });
}

export interface GetAppointmentTypeResult {
  data: AppointmentType;
}

export async function getAppointmentType(
  clientUuid: string,
  appointmentTypeId: string,
) {
  const url = `/appointment_type/${clientUuid}/${appointmentTypeId}`;
  const response = await appointmentAxios.get<GetAppointmentTypeResult>(url);
  return response.data;
}

export interface GetAppointmentDiscounts {
  data: DiscountType[];
  hasNext?: boolean;
  hasPrevious?: boolean;
  next?: string;
  previous?: string;
}

export async function getAppointmentDiscounts(clientUuid: string) {
  return fetchAllPages(async (next?: string) => {
    const url = `/appointment_discount/${clientUuid}`;
    const response = await appointmentAxios.get<GetAppointmentDiscounts>(url, {
      params: { next },
    });
    return response.data;
  });
}

export interface GetAppointmentDiscount {
  data: DiscountType;
  hasNext?: boolean;
  hasPrevious?: boolean;
  next?: string;
  previous?: string;
}

export async function getAppointmentDiscount(
  clientUuid: string,
  appointmentDiscountUuid: string,
) {
  const url = `/appointment_discount/${clientUuid}/${appointmentDiscountUuid}`;
  const response = await appointmentAxios.get<GetAppointmentDiscount>(url);
  return response.data;
}

export interface GetAppointmentPackages {
  data: AppointmentPackage[];
  hasNext?: boolean;
  hasPrevious?: boolean;
  next?: string;
  previous?: string;
}

export function getAppointmentPackages(clientUuid: string) {
  return fetchAllPages(async (next?: string) => {
    const url = `/appointment_package/${clientUuid}`;
    const response = await appointmentAxios.get<GetAppointmentPackages>(url, {
      params: { next },
    });
    return response.data;
  });
}

export interface GetAppointmentPackageResult {
  data: {
    taxId?: string;
    staffPayType: string;
    recurringInterval?: string;
    name: string;
    discount?: {
      objectType: string;
      discountValue: number;
      createdAt: number;
      name: string;
      numberOfRebills: number;
      discountCode: string;
      appliesForXRebills: boolean;
      client: string;
      updatedAt: number;
      discountType: string;
      uuid: string;
      SK: string;
      appliesForever: boolean;
      description: string;
      PK: string;
    };
    taxName?: string;
    numberOfSessions: number;
    isRecurring: boolean;
    hasTax: boolean;
    policydocuments: {
      uuid: string;
    }[];
    expirationForEachAppointmentCredit: number;
    createdAt: string;
    packagePrice: number;
    availableOnMemberApp: boolean;
    appointmentType: {
      name: string;
      uuid: string;
      color: string;
    };
    taxPrice: number;
    client: string;
    updatedAt: string;
    staffPay: number;
    uuid: string;
    description: string;
    isFree: boolean;
    longDescription?: string;
  };
}

export async function getAppointmentPackage(
  clientUuid: string,
  appointmentPackageUuid: string,
) {
  const url = `/appointment_package/${clientUuid}/${appointmentPackageUuid}`;
  const response = await appointmentAxios.get<GetAppointmentPackageResult>(url);
  return response.data;
}

export interface PostDuplicateAppointmentDiscountResult {
  data: AppointmentDiscount;
}

export async function postDuplicateAppointmentDiscount(
  clientUuid: string,
  appointmentDiscountId: string,
  appointmentDiscountName: string,
) {
  const url = `/appointment_discount/${clientUuid}/${appointmentDiscountId}/duplicate`;
  const response =
    await appointmentAxios.post<PostDuplicateAppointmentDiscountResult>(url, {
      name: appointmentDiscountName,
    });
  return response.data;
}

export interface PostDuplicateAppointmentTypeResult {
  data: AppointmentType;
}

export async function postDuplicateAppointmentType(
  clientUuid: string,
  appointmentTypeId: string,
  appointmentTypeName: string,
) {
  const url = `appointment_type/${clientUuid}/${appointmentTypeId}/duplicate`;
  const response =
    await appointmentAxios.post<PostDuplicateAppointmentTypeResult>(url, {
      name: appointmentTypeName,
    });
  return response.data;
}

export interface PostDuplicateAppointmentPackageResult {
  data: AppointmentPackage;
}

export async function postDuplicateAppointmentPackage(
  clientUuid: string,
  appointmentPackageId: string,
  appointmentPackageName: string,
) {
  const url = `/appointment_package/${clientUuid}/${appointmentPackageId}/duplicate`;
  const response =
    await appointmentAxios.post<PostDuplicateAppointmentPackageResult>(url, {
      name: appointmentPackageName,
    });
  return response.data;
}

interface ExcludeAppointmentDiscountParams {
  clientUuid: string;
  appointmentDiscountId: string;
}

export async function excludeAppointmentDiscount({
  clientUuid,
  appointmentDiscountId,
}: ExcludeAppointmentDiscountParams) {
  const url = `/appointment_discount/${clientUuid}/${appointmentDiscountId}`;
  await appointmentAxios.delete<void>(url);
}

interface ExcludeAppointmentTypeParams {
  clientUuid: string;
  appointmentTypeId: string;
}

export async function excludeAppointmentType({
  clientUuid,
  appointmentTypeId,
}: ExcludeAppointmentTypeParams) {
  const url = `/appointment_type/${clientUuid}/${appointmentTypeId}`;
  await appointmentAxios.delete<void>(url);
}

interface ExcludeAppointmentPackageParams {
  clientUuid: string;
  appointmentPackageId: string;
}

export async function excludeAppointmentPackage({
  clientUuid,
  appointmentPackageId,
}: ExcludeAppointmentPackageParams) {
  const url = `/appointment_package/${clientUuid}/${appointmentPackageId}`;
  await appointmentAxios.delete<void>(url);
}

export interface PostAppointmentTypePayload {
  name: string;
  duration: number;
  color: string;
  staff: {
    uuid: string;
  }[];
  bufferBeforeAppointment: string;
  bufferAfterAppointment: string;
  availabilityIncrements: string;
  minimumSchedulingNotice: number;
  lateCancel: number;
  hasNextSteps: boolean;
  nextStepsLabel: string;
  nextStepsUrl: string;
  availability: {
    dayOfTheWeek: string;
    startTime: string;
    endTime: string;
  }[];
}

export interface PostAppointmentTypeVariables {
  clientUuid: string;
  payload: PostAppointmentTypePayload;
}

export interface PostAppointmentTypeResult {
  data: AppointmentType;
}

export async function postAppointmentType({
  clientUuid,
  payload,
}: PostAppointmentTypeVariables) {
  const url = `/appointment_type/${clientUuid}`;
  const response = await appointmentAxios.post<PostAppointmentTypeResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PatchAppointmentTypeVariables {
  clientUuid: string;
  appointmentTypeId: string;
  payload: PatchAppointmentTypePayload;
}

export interface PatchAppointmentTypePayload {
  staff: {
    uuid: string;
  }[];
  emailNotificationSubject: string;
  sendEmailReminder: boolean;
  sendTextReminder: boolean;
  bufferBeforeAppointment: string;
  lateCancel: number;
  textReminderMessage: string;
  sendEmailNotification: boolean;
  name: string;
  availability: {
    dayOfTheWeek: string;
    startTime: string;
    endTime: string;
  }[];
  minimumSchedulingNotice: number;
  hasNextSteps: boolean;
  textReminderTiming: number;
  nextStepsUrl: string;
  availabilityIncrements: string;
  emailReminderSubject: string;
  bufferAfterAppointment: string;
  nextStepsLabel: string;
  duration: number;
  color: string;
  emailReminderTiming: number;
  createdAt: string;
  message: string;
  emailReminderMessage: string;
  client: string;
  updatedAt: string;
  uuid: string;
}

export interface PatchAppointmentTypeResult {
  data: AppointmentType;
}

export async function pathAppointmentType({
  clientUuid,
  appointmentTypeId,
  payload,
}: PatchAppointmentTypeVariables) {
  const url = `/appointment_type/${clientUuid}/${appointmentTypeId}`;
  const response = await appointmentAxios.patch<PatchAppointmentTypeResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PostAppointmentPackagePayload {
  isFree: boolean;
  isRecurring: boolean;
  name: string;
  description: string;
  longDescription?: string;
  appointmentType: string;
  numberOfSessions: number;
  expirationForEachAppointmentCredit: number;
  availableOnMemberApp: boolean;
  packagePrice: number;
  hasTax: boolean;
  taxId: string;
  taxName: string;
  taxPrice?: number;
  staffPay: number;
  staffPayType: string;
  discount?: string;
  policydocuments?: {
    uuid: string;
  }[];
}

export interface PostAppointmentPackageResult {
  data: AppointmentPackage;
}

export interface PostAppointmentPackageVariables {
  clientUuid: string;
  payload: PostAppointmentPackagePayload;
}

export async function postAppointmentPackage({
  clientUuid,
  payload,
}: PostAppointmentPackageVariables) {
  const url = `/appointment_package/${clientUuid}`;
  const response = await appointmentAxios.post<PostAppointmentPackageResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PatchAppointmentPackagePayload {
  taxId: string;
  taxName: string;
  taxPrice?: number;
  staffPayType: string;
  name: string;
  discount: string;
  numberOfSessions: number;
  isRecurring: boolean;
  hasTax: boolean;
  policydocuments?: {
    uuid: string;
  }[];
  expirationForEachAppointmentCredit: number;
  packagePrice: number;
  availableOnMemberApp: boolean;
  appointmentType: string;
  staffPay: number;
  description: string;
  longDescription?: string;
  isFree: boolean;
}
export interface PatchAppointmentPackageResult {
  data: AppointmentPackage;
}

export interface PatchAppointmentPackageVariables {
  clientUuid: string;
  appointmentPackageId: string;
  payload: PatchAppointmentPackagePayload;
}

export async function patchAppointmentPackage({
  clientUuid,
  appointmentPackageId,
  payload,
}: PatchAppointmentPackageVariables) {
  const url = `/appointment_package/${clientUuid}/${appointmentPackageId}`;
  const response = await appointmentAxios.patch<PatchAppointmentPackageResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PostAppointmentDiscountResult {
  data: {
    uuid: string;
    client: string;
    name: string;
    description: string;
    appliesForever: boolean;
    appliesForXRebills: boolean;
    numberOfRebills: number;
    discountCode: string;
    discountType: string;
    discountValue: number;
    createdAt: string;
    updatedAt: string;
  };
}

export interface PostAppointmentDiscountPayload {
  name: string;
  description?: string;
  discountValue: number;
  discountType: string;
  numberOfRebills: number;
  appliesForever: boolean;
  appliesForXRebills: boolean;
}

export interface PostAppointmentDiscountVariables {
  clientUuid: string;
  payload: PostAppointmentDiscountPayload;
}

export async function postAppointmentDiscount({
  clientUuid,
  payload,
}: PostAppointmentDiscountVariables) {
  const url = `/appointment_discount/${clientUuid}`;
  const response = await appointmentAxios.post<PostAppointmentDiscountResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PostAppointmentListViewResult {
  data: {
    date: string;
    entries: AppointmentEvent[];
  }[];
  hasNext?: boolean;
  hasPrevious?: boolean;
  next?: string;
  previous?: string;
}

export async function postAppointmentListView(
  clientUuid: string,
  timezone: string,
  from?: string,
  to?: string,
) {
  const url = `/appointment_schedule/${clientUuid}/listview`;
  const body = {
    from,
    to,
    timeZone: timezone,
  };
  const response = await appointmentAxios.post<PostAppointmentListViewResult>(
    url,
    body,
  );
  return response.data?.data?.reduce(
    (prev, cur) => [...prev, ...cur.entries],
    [],
  );
}

export interface PatchAppointmentDiscountPayload {
  name: string;
  description?: string;
  discountValue: number;
  discountType: string;
  numberOfRebills?: number;
  appliesForever?: boolean;
  appliesForXRebills?: boolean;
}

export interface PatchAppointmentDiscountResult {
  data: {
    discountValue: number;
    createdAt: string;
    name: string;
    numberOfRebills: number;
    discountCode: string;
    appliesForXRebills: boolean;
    client: string;
    updatedAt: string;
    discountType: string;
    uuid: string;
    appliesForever: boolean;
    description: string;
  };
}

export interface PatchAppointmentDiscountVariables {
  clientUuid: string;
  appointmentDiscountId: string;
  payload: PatchAppointmentDiscountPayload;
}

export async function patchAppointmentDiscount({
  clientUuid,
  appointmentDiscountId,
  payload,
}: PatchAppointmentDiscountVariables) {
  const url = `/appointment_discount/${clientUuid}/${appointmentDiscountId}`;
  const response = await appointmentAxios.patch<PatchAppointmentDiscountResult>(
    url,
    payload,
  );
  return response.data;
}

export interface HistorySchedule {
  staff: {
    uuid?: string;
    firstName: string;
    email?: string;
    lastName: string;
    first_name?: string;
    last_name?: string;
  };
  eventType: string;
  eventStatus: AppointmentStatus;
  uuid: string;
  appointmentType: {
    staff: {
      uuid: string;
    }[];
    emailNotificationSubject: string;
    sendEmailReminder: boolean;
    sendTextReminder: boolean;
    bufferBeforeAppointment: number;
    lateCancel: number;
    textReminderMessage: string;
    sendEmailNotification: boolean;
    name: string;
    availability: {
      dayOfTheWeek: string;
      startTime: string;
      endTime: string;
    }[];
    minimumSchedulingNotice: number;
    hasNextSteps: boolean;
    textReminderTiming: number;
    nextStepsUrl: string;
    availabilityIncrements: number;
    emailReminderSubject: string;
    bufferAfterAppointment: number;
    SK: string;
    duration: number;
    nextStepsLabel: string;
    color: string;
    location?: string;
    emailReminderTiming: number;
    createdAt: string | number;
    message: string;
    emailReminderMessage: string;
    client: string;
    updatedAt: string | number;
    uuid: string;
    description?: string;
    PK: string;
  };
  appointmentCredit?: {
    uuid: string;
    usedAt: string;
    canExpire: boolean;
    expiresAt: string;
  };
  startTimeZone?: string;
  endTimeZone?: string;
  createdAt?: string;
  updatedAt?: string;
  cancelledAt?: string;
  startTime: string;
  endTime: string;
  paidAt?: string;
  price?: number;
  expiresAt?: string;
  isLateCancel?: boolean;
}
export interface GetAppointmentHistoryResult {
  credits?: number;
  schedule: HistorySchedule[];
}

export async function getAppointmentHistory(
  clientUuid: string,
  memberId: string,
) {
  const url = `/appointment_schedule/${clientUuid}/${memberId}/history`;
  const response = await appointmentAxios.get<GetAppointmentHistoryResult>(url);
  return response.data;
}

export interface PostChangeAppointmentStaffPayload {
  user_uuid: string;
}

export interface PostChangeAppointmentStaffResult {
  message: string;
  data: {
    uuid: string;
    firstName: string;
    email: string;
    lastName: string;
    first_name: string;
    last_name: string;
  };
}

interface PostChangeAppointmentStaffVariables {
  clientUuid: string;
  appointmentId: string;
  payload: PostChangeAppointmentStaffPayload;
}

export async function postChangeAppointmentStaff({
  clientUuid,
  appointmentId,
  payload,
}: PostChangeAppointmentStaffVariables) {
  const url = `/appointment_schedule/${clientUuid}/${appointmentId}/changestaff`;
  const body = {
    staff: payload.user_uuid,
  };
  const response =
    await appointmentAxios.post<PostChangeAppointmentStaffResult>(url, body);
  return response.data;
}

export interface PostAppointmentCheckInVariables {
  clientUuid: string;
  appointmentId: string;
}

export interface PostAppointmentCheckInResult {
  data: string;
}

export async function postAppointmentCheckIn({
  clientUuid,
  appointmentId,
}: PostAppointmentCheckInVariables) {
  const url = `/appointment_schedule/${clientUuid}/${appointmentId}/checkin`;
  const response =
    await appointmentAxios.post<PostAppointmentCheckInResult>(url);
  return response.data;
}

export interface PostMarkNoShowVariables {
  clientUuid: string;
  appointmentId: string;
}

export interface PostMarkNoShowResult {
  data: string;
}

export async function postMarkNoShow({
  clientUuid,
  appointmentId,
}: PostMarkNoShowVariables) {
  const url = `/appointment_schedule/${clientUuid}/${appointmentId}/noshow`;
  const response = await appointmentAxios.post<PostMarkNoShowResult>(url);
  return response.data;
}

export interface PostCancelAppointmentResult {
  data: string;
}

export interface PostCancelAppointmentVariables {
  clientUuid: string;
  appointmentUuid: string;
  payload?: {
    keepCredit?: boolean;
    isLateCancel?: boolean;
    emailStaff: boolean;
    emailCustomer: boolean;
  };
}

export async function postCancelAppointment({
  clientUuid,
  appointmentUuid,
  payload,
}: PostCancelAppointmentVariables) {
  const url = `/appointment_schedule/${clientUuid}/${appointmentUuid}/cancel`;
  const response = await appointmentAxios.post<PostCancelAppointmentResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PostRescheduleAppointmentPayload {
  startTime: Date;
  endTime: Date;
  startTimeZone: string;
  endTimeZone: string;
}

export interface PostRescheduleAppointmentResult {
  message: string;
  data: {
    date: string;
    entries: AppointmentEvent[];
  };
}

export interface PostRescheduleAppointmentVariables {
  clientUuid: string;
  appointmentUuid: string;
  payload: PostRescheduleAppointmentPayload;
}

export async function postRescheduleAppointment({
  clientUuid,
  appointmentUuid,
  payload,
}: PostRescheduleAppointmentVariables) {
  const url = `/appointment_schedule/${clientUuid}/${appointmentUuid}/reschedule`;
  const response = await appointmentAxios.post<PostRescheduleAppointmentResult>(
    url,
    payload,
  );
  return response.data;
}

export interface PostAppointmentMultipleCalendarViewResult {
  blockedData: {
    start: number;
    end: number;
    key: string;
    source?: string;
    rawStart?: string;
    rawEnd?: string;
  }[];
  eventData: {
    id: string;
    key: string;
    staff: {
      uuid: string;
      firstName: string;
      email: string;
      lastName: string;
      first_name: string;
      last_name: string;
    };
    startTime: number;
    duration: number;
    name: string;
    eventStatus: string;
    appointmentType: {
      staff: {
        uuid: string;
      }[];
      emailNotificationSubject: string;
      sendEmailReminder: boolean;
      sendTextReminder: boolean;
      bufferBeforeAppointment: string;
      lateCancel: number;
      textReminderMessage: string;
      sendEmailNotification: boolean;
      name: string;
      availability: {
        dayOfTheWeek: string;
        startTime: string;
        endTime: string;
      }[];
      minimumSchedulingNotice: number;
      hasNextSteps: boolean;
      textReminderTiming: number;
      nextStepsUrl: string;
      availabilityIncrements: string;
      emailReminderSubject: string;
      bufferAfterAppointment: number;
      SK: string;
      nextStepsLabel: string;
      duration: number;
      color: string;
      emailReminderTiming: number;
      createdAt: string;
      message: string;
      emailReminderMessage: string;
      client: string;
      updatedAt: string;
      uuid: string;
      PK: string;
    };
    eventType: string;
    start: string;
    end: string;
    day: string;
    uuid: string;
    user: {
      uuid: string;
      firstName: string;
      email: string;
      lastName: string;
      first_name: string;
      last_name: string;
    };
    appointmentCredit?: {
      uuid: string;
      usedAt: string;
      canExpire: boolean;
      expiresAt: string;
    };
  }[];
  start?: string;
  end?: string;
  ts?: number;
}

export async function postAppointmentMultipleCalendar(
  clientUuid: string,
  from?: string,
  to?: string,
  timezone?: string,
) {
  const url = `/appointment_schedule/${clientUuid}/multiplecalendarview`;
  const body = {
    from,
    to,
    timeZone: timezone,
  };
  const response =
    await appointmentAxios.post<PostAppointmentMultipleCalendarViewResult>(
      url,
      body,
    );
  return response.data;
}

export interface PostAppointmentSingleStaffCalendarResult {
  blockedData: {
    start: number;
    end: number;
    key: string;
  }[];
  eventData: AppointmentEvent[];
  start?: number;
  end?: number;
}

export async function postAppointmentSingleStaffCalendar(
  clientUuid: string,
  staffId: string,
  from?: string,
  to?: string,
  timeZone?: string,
) {
  const url = `/appointment_schedule/${clientUuid}/${staffId}/calendar`;
  const body = {
    from,
    to,
    timeZone,
  };
  const response =
    await appointmentAxios.post<PostAppointmentSingleStaffCalendarResult>(
      url,
      body,
    );
  return response.data;
}

export interface GetAppointmentTypeStaffResult {
  staff: Staff[];
}

export async function getAppointmentTypeStaff(
  clientUuid: string,
  appointmentTypeUuid: string,
) {
  const url = `/appointment_type/${clientUuid}/${appointmentTypeUuid}/staff`;
  const response =
    await appointmentAxios.get<GetAppointmentTypeStaffResult>(url);
  return response.data;
}

export interface PostAppointmentWorkingHoursPayload {
  uuid?: string;
  workingHours: {
    dayOfTheWeek: string;
    startTime: string;
    endTime: string;
  }[];
  timezone: string;
}
export interface PostAppointmentWorkingHoursVariable {
  clientUuid: string;
  memberId: string;
  payload: PostAppointmentWorkingHoursPayload;
}

export interface PostAppointmentWorkingHoursResult {
  data: {
    client: string;
    user: string;
    workingHours: {
      dayOfTheWeek: string;
      startTime: string;
      endTime: string;
    }[];
    timezone: string;
    uuid: string;
  };
}

export async function postAppointmentWorkingHours({
  clientUuid,
  memberId,
  payload,
}: PostAppointmentWorkingHoursVariable) {
  const url = `/appointment_whs/${clientUuid}/${memberId}`;
  const response =
    await appointmentAxios.post<PostAppointmentWorkingHoursResult>(
      url,
      payload,
    );
  return response.data;
}

export interface DeleteAppointmentWorkingHoursVariable {
  clientUuid: string;
  memberId: string;
  uuid: string;
}

export async function deleteAppointmentWorkingHours({
  clientUuid,
  memberId,
  uuid,
}: DeleteAppointmentWorkingHoursVariable) {
  const url = `/appointment_whs/${clientUuid}/${memberId}/${uuid}`;
  await appointmentAxios.delete(url);
}

export interface GetAppointmentWorkingHoursResult {
  data: {
    timezone: string;
    uuid: string;
    workingHours: {
      dayOfTheWeek: string;
      startTime: string;
      endTime: string;
    }[];
  } | null;
}

export async function getAppointmentWorkingHours(
  clientUuid: string,
  memberId: string,
) {
  const url = `/appointment_whs/${clientUuid}/${memberId}`;
  try {
    const response =
      await appointmentAxios.get<GetAppointmentWorkingHoursResult>(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error && error.message === "Not Found") {
      return { data: null };
    }
    throw error;
  }
}

export interface GetAppointmentCreditsResult {
  data: {
    count: number;
  };
}

export async function getAppointmentCredits(
  clientUuid: string,
  userUuid: string,
  appointmentTypeUuid: string,
) {
  const url = `/appointment_credit/${clientUuid}/${userUuid}/${appointmentTypeUuid}`;
  const response = await appointmentAxios.get<GetAppointmentCreditsResult>(url);
  return response.data;
}

export interface PostAppointmentCreditVariables {
  clientUuid: string;
  payload: {
    purchaseType: "PACKAGE" | "CREDIT";
    userId: string;
    paymentId?: string;
    appointmentType?: string;
    appointmentPackage?: string;
    appointmentDiscount?: string;
    creditNumber?: number;
    creditExpiry?: number;
    price?: number;
    taxId?: string;
  };
}

export async function postAppointmentCreditPurchase({
  clientUuid,
  payload,
}: PostAppointmentCreditVariables) {
  const url = `/appointment_credit/${clientUuid}/purchase`;
  const response =
    await appointmentAxios.post<PostAppointmentWorkingHoursResult>(
      url,
      payload,
    );
  return response.data;
}

export interface GetAppointmentResult {
  staff: {
    uuid: string;
    firstName: string;
    email: string;
    lastName: string;
    first_name: string;
    last_name: string;
    phone: string;
  };
  objectType: string;
  eventStatus: AppointmentStatus;
  createdAt: string;
  cancelledAt?: string;
  user: {
    uuid: string;
    firstName: string;
    email: string;
    lastName: string;
    first_name: string;
    last_name: string;
    phone: string;
  };
  startTime: string;
  startTimestamp: number;
  attendees: string[];
  appointmentType: {
    staff: {
      uuid: string;
    }[];
    emailNotificationSubject: string;
    sendEmailReminder: boolean;
    sendTextReminder: boolean;
    bufferBeforeAppointment: string;
    lateCancel: number;
    name: string;
    sendEmailNotification: boolean;
    textReminderMessage: string;
    availability: {
      dayOfTheWeek: string;
      startTime: string;
      endTime: string;
    }[];
    minimumSchedulingNotice: number;
    hasNextSteps: boolean;
    textReminderTiming: number;
    availabilityIncrements: string;
    nextStepsUrl: string;
    emailReminderSubject: string;
    bufferAfterAppointment: string;
    SK: string;
    duration: number;
    nextStepsLabel: string;
    color: string;
    objectType: string;
    notifyStaffMember: boolean;
    policydocuments: {
      uuid: string;
    }[];
    emailReminderTiming: number;
    createdAt: string;
    message: string;
    emailReminderMessage: string;
    client: string;
    updatedAt: string;
    uuid: string;
    PK: string;
    numberOfSessions: number;
  };
  endTime: string;
  client: string;
  updatedAt: string;
  appointmentCredit?: {
    uuid: string;
    usedAt: string;
    canExpire: boolean;
    expiresAt: string;
    price: number;
    tax: number;
    discount: number;
    total: number;
  };
  nowshowedAt: number;
  uuid: string;
  SK: string;
  eventType: string;
  endTimeStamp: number;
  PK: string;
  startTimeZone: string;
  endTimeZone: string;
}

export async function getAppointment(
  clientUuid: string,
  appointmentUUid: string,
) {
  const url = `/appointment_schedule/${clientUuid}/${appointmentUUid}`;
  const response = await appointmentAxios.get<GetAppointmentResult>(url);
  return response.data;
}

interface ExcludeAppointmentCreditVariables {
  clientUuid: string;
  userUuid: string;
  appointmentUuid: string;
}

export async function excludeAppointmentCredit({
  clientUuid,
  userUuid,
  appointmentUuid,
}: ExcludeAppointmentCreditVariables) {
  const url = `/appointment_credit/${clientUuid}/${userUuid}/${appointmentUuid}/remove`;
  await appointmentAxios.post<void>(url);
}

export interface PostUpdateExpireAppointmentVariables {
  clientUuid: string;
  userUuid: string;
  appointmentUuid: string;
  payload: {
    date: string;
  };
}

export async function postUpdateExpireAppointment({
  clientUuid,
  userUuid,
  appointmentUuid,
  payload,
}: PostUpdateExpireAppointmentVariables) {
  const url = `/appointment_credit/${clientUuid}/${userUuid}/${appointmentUuid}/update_expiry`;
  const response = await appointmentAxios.post<PostRescheduleAppointmentResult>(
    url,
    payload,
  );
  return response.data;
}
